import React, { useState } from "react";
import { Plus } from "lucide-react";
import SimpleBanner from "../../components/common/SimpleBanner";

const faqs = [
  {
    question: "What AI solutions do you offer?",
    answer:
      "We offer a wide range of AI solutions including machine learning models, AI-powered analytics, natural language processing, computer vision, and custom AI application development tailored to your business needs.",
  },
  {
    question: "How can AI improve my business operations?",
    answer:
      "AI helps automate repetitive tasks, analyze large datasets, improve decision-making, enhance customer experiences, and optimize operational efficiency, leading to reduced costs and increased productivity.",
  },
  {
    question: "Is AI integration safe and secure?",
    answer:
      "Yes, we follow industry best practices for data security and compliance. Our AI systems are designed with secure architectures, data encryption, and privacy-focused workflows to ensure your data remains protected.",
  },
  {
    question: "Do I need technical knowledge to use AI solutions?",
    answer:
      "No. Our AI solutions are built with user-friendly interfaces and seamless integrations. We also provide full onboarding, documentation, and support to ensure smooth adoption.",
  },
  {
    question: "Can AI solutions be customized for my industry?",
    answer:
      "Absolutely. We design and train AI models specifically for your industry, whether it's healthcare, finance, e-commerce, travel, or manufacturing, ensuring maximum relevance and impact.",
  },
];

const FAQ = () => {
  const [openAccordion, setOpenAccordion] = useState(null);

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  return (
    <>
    <SimpleBanner title="FAQ" subheading="Home / FAQ" />
    <div className="max-w-6xl mx-auto px-4 py-12">
  {/* Header Section */}
  <div className="text-center mb-12 animate-fade-in">
    <div className="inline-flex items-center justify-center mb-4">
      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 animate-pulse"></div>
      <div className="mx-4 text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 uppercase tracking-wider">
        FAQ Center
      </div>
      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 animate-pulse"></div>
    </div>
    
    <h1 className="text-4xl md:text-5xl font-bold mb-6">
      <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#d431ac] via-purple-600 to-[#425bfc]">
        Find Your Answers
      </span>
    </h1>
    
    <p className="text-gray-600 text-lg max-w-2xl mx-auto">
      Everything you need to know about our services. Can't find what you're looking for? 
      <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 font-medium ml-1">
        Contact our support team.
      </span>
    </p>
  </div>

  {/* Stats Bar */}
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
    <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-6 text-center transform transition duration-300 hover:scale-105 hover:shadow-lg">
      <div className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-purple-500">
        {faqs.length}+
      </div>
      <div className="text-gray-600 mt-2">Questions Answered</div>
    </div>
    <div className="bg-gradient-to-r from-purple-50 to-cyan-50 rounded-2xl p-6 text-center transform transition duration-300 hover:scale-105 hover:shadow-lg">
      <div className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-cyan-500">
        24/7
      </div>
      <div className="text-gray-600 mt-2">Support Available</div>
    </div>
    <div className="bg-gradient-to-r from-cyan-50 to-pink-50 rounded-2xl p-6 text-center transform transition duration-300 hover:scale-105 hover:shadow-lg">
      <div className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-500 to-pink-500">
        98%
      </div>
      <div className="text-gray-600 mt-2">Satisfaction Rate</div>
    </div>
  </div>

  {/* FAQ Accordions */}
  <div className="space-y-6">
    {faqs.map((item, index) => (
      <div
        key={index}
        className={`rounded-[28px] overflow-hidden transform transition-all duration-500 hover:shadow-2xl hover:scale-[1.005] ${
          openAccordion === index
            ? "p-[3px] bg-gradient-to-r from-[#d431ac] via-purple-500 to-[#425bfc] shadow-xl"
            : "bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200/50 hover:border-purple-200"
        } animate-slide-up`}
        style={{ animationDelay: `${index * 0.07}s` }}
      >
        {/* Inner container */}
        <div
          className={`${
            openAccordion === index
              ? "bg-white/95 backdrop-blur-sm rounded-[25px]"
              : "rounded-[28px]"
          }`}
        >
          {/* Header */}
          <button
            onClick={() => toggleAccordion(index)}
            className="w-full flex items-center justify-between p-6 sm:p-8 text-left transition duration-300 group"
          >
            <div className="flex items-start space-x-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mt-1 ${
                openAccordion === index 
                  ? "bg-gradient-to-br from-pink-500 to-purple-500" 
                  : "bg-gradient-to-br from-gray-200 to-gray-300 group-hover:from-pink-100 group-hover:to-purple-100"
              } transition-all duration-300`}>
                <span className={`font-semibold text-sm ${
                  openAccordion === index ? "text-white" : "text-gray-600 group-hover:text-purple-600"
                }`}>
                  {index + 1}
                </span>
              </div>
              <div className="text-left">
                <span className="font-semibold text-gray-900 text-lg sm:text-xl md:text-2xl group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-pink-600 group-hover:to-purple-600 transition-all duration-300">
                  {item.question}
                </span>
              </div>
            </div>

            <div className={`transform transition-all duration-500 flex-shrink-0 ml-4 ${
              openAccordion === index 
                ? "rotate-45 scale-110" 
                : "group-hover:scale-110"
            }`}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 ${
                openAccordion === index
                  ? "bg-gradient-to-br from-pink-500 to-purple-500 shadow-lg"
                  : "bg-gradient-to-br from-gray-200 to-gray-300 group-hover:from-pink-500 group-hover:to-purple-500"
              }`}>
                <Plus className={`w-6 h-6 transition-all duration-300 ${
                  openAccordion === index 
                    ? "text-white" 
                    : "text-gray-500 group-hover:text-white"
                }`} />
              </div>
            </div>
          </button>

          {/* Content */}
          <div
            className={`overflow-hidden transition-all duration-700 ease-in-out ${
              openAccordion === index
                ? "max-h-96 opacity-100"
                : "max-h-0 opacity-0"
            }`}
          >
            <div className="px-6 sm:px-8 pb-8 pt-0">
              <div className="border-l-4 border-gradient-to-b from-pink-500 to-purple-500 pl-6 ml-4 mt-2">
                <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                  {item.answer}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>

  {/* CTA Section */}
  <div className="mt-16 text-center">
    <div className="bg-gradient-to-r from-pink-50 via-purple-50 to-cyan-50 rounded-3xl p-8 md:p-12 max-w-4xl mx-auto border border-gray-200/50 shadow-lg">
      <h3 className="text-2xl md:text-3xl font-bold mb-4">
        Still have questions?
      </h3>
      <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
        We're here to help. Get in touch and our support team will answer your questions.
      </p>
      <button className="group relative inline-flex items-center justify-center px-8 py-4 font-semibold text-white rounded-2xl overflow-hidden transition-all duration-300 bg-gradient-to-r from-[#d431ac] to-[#425bfc] hover:shadow-xl hover:shadow-purple-500/30 transform hover:-translate-y-1">
        <span>Contact Support</span>
        <svg className="w-5 h-5 ml-3 transform group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
        </svg>
      </button>
    </div>
  </div>
</div>
    </>
  );
};

export default FAQ;
